import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtimeEvent } from "../lib/realtime";
import { Send, X, Minus, Paperclip, Check } from "@/components/icons";
import ReferralQuickModal from "./ReferralQuickModal";
import ReferralChatCard from "./ReferralChatCard";
import NewAppointmentModal from "./appointment/NewAppointmentModal";

/**
 * ChatWindow — Ventana flotante estilo MSN/moderno con header centrado,
 * avatar grande con verificación, especialidad, burbujas y barra inferior
 * con íconos (cámara, referido) + input pill + botón enviar.
 *
 * Props:
 *   contact: { id, name, photo_url, online, specialty, role }
 *   onClose(id)
 *   onMinimize(id)
 *   style: posicionamiento (right offset)
 */
export default function ChatWindow({ contact, onClose, onMinimize, style }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [peerTyping, setPeerTyping] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [apptPatientId, setApptPatientId] = useState(null);
  const bottomRef = useRef();
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const firstScrollRef = useRef(true);

  const canReferTo =
    user?.role === "doctor" && contact?.role === "doctor";

  const specialty =
    contact?.specialty ||
    (contact?.role === "doctor"
      ? "Doctor"
      : contact?.role === "secretary"
        ? "Secretaria"
        : contact?.role === "admin"
          ? "Administrador"
          : "");

  const fetchMsg = async () => {
    try {
      const { data } = await api.get(`/chat/${contact.id}`);
      setMessages(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchMsg();
    const t = setInterval(fetchMsg, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id]);

  useRealtimeEvent("chat:new", (m) => {
    if (m.from_user_id === contact.id || m.to_user_id === contact.id) {
      fetchMsg();
    }
  });
  useRealtimeEvent("chat:sent", (m) => {
    if (m.to_user_id === contact.id) fetchMsg();
  });

  useRealtimeEvent("chat:typing", (m) => {
    if (!m || m.from_user_id !== contact.id) return;
    setPeerTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => setPeerTyping(false), 2500);
  });

  useEffect(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({
      behavior: firstScrollRef.current ? "auto" : "smooth",
      block: "end",
    });
    if (messages.length > 0) firstScrollRef.current = false;
  }, [messages]);

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = now;
    api.post("/chat/typing", { to_user_id: contact.id }).catch(() => {});
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post("/chat", {
      to_user_id: contact.id,
      content: text.trim(),
    });
    setMessages((m) => [...m, data]);
    setText("");
    setPeerTyping(false);
  };

  const initials = (contact.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");

  const formatHour = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  return (
    <>
      <div
        className="fixed z-[54] bottom-0 h-[604px] w-[340px] bg-white rounded-tl-[22px] shadow-[0_8px_32px_rgba(0,0,0,0.18)] border border-r-0 border-b-0 border-black/[0.06] flex flex-col overflow-hidden chat-window-pop"
        style={style}
        data-testid={`chat-window-${contact.id}`}
      >
        {/* Botones esquina (minimizar / cerrar) */}
        <div className="absolute top-2.5 right-2.5 flex items-center gap-0.5 z-10">
          <button
            type="button"
            onClick={() => onMinimize(contact.id)}
            data-testid={`chat-window-minimize-${contact.id}`}
            title="Minimizar"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-black/50 hover:bg-black/[0.05] hover:text-black transition"
          >
            <Minus className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>
          <button
            type="button"
            onClick={() => onClose(contact.id)}
            data-testid={`chat-window-close-${contact.id}`}
            title="Cerrar"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-black/50 hover:bg-black/[0.05] hover:text-black transition"
          >
            <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>
        </div>

        {/* Header: avatar centrado con verificación + nombre + especialidad */}
        <div className="pt-6 pb-3 px-4 flex flex-col items-center text-center">
          <div className="relative">
            <span className="w-16 h-16 rounded-full overflow-hidden block bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center ring-1 ring-black/[0.04]">
              {contact.photo_url ? (
                <img
                  src={contact.photo_url}
                  alt={contact.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-[18px]">
                  {initials}
                </span>
              )}
            </span>
            {contact.role === "doctor" && (
              <span
                className="absolute -right-1 -bottom-0.5 w-5 h-5 rounded-full bg-[#22C55E] flex items-center justify-center ring-2 ring-white"
                title="Doctor verificado"
              >
                <Check
                  className="w-3 h-3 text-white"
                  strokeWidth={3}
                  weight="bold"
                />
              </span>
            )}
          </div>
          <div className="mt-2.5 text-[14.5px] font-bold text-black leading-tight truncate max-w-full">
            {contact.name}
          </div>
          <div
            className="text-[11.5px] text-black/55 mt-0.5 min-h-[14px]"
            data-testid={`chat-window-status-${contact.id}`}
          >
            {peerTyping ? (
              <span className="inline-flex items-center gap-1 text-[#0071E3] font-semibold">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                <span className="ml-1">está escribiendo…</span>
              </span>
            ) : (
              <>
                {specialty}
                {contact.online && (
                  <span className="ml-1.5 text-[#22C55E] font-semibold">
                    · En línea
                  </span>
                )}
              </>
            )}
          </div>
          <div className="mt-3 w-full h-px bg-black/[0.06]" />
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-2 bg-white">
          {messages.length === 0 && (
            <div className="text-[12px] text-black/40 text-center py-6">
              Sin mensajes todavía.
            </div>
          )}
          {messages.map((m, idx) => {
            const mine = m.from_user_id === user.id;
            const prev = messages[idx - 1];
            const samePrev =
              prev && prev.from_user_id === m.from_user_id;
            const showAvatar = !mine && !samePrev;

            // Burbuja especial para referidos
            if (m.type === "referral") {
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <ReferralChatCard
                    message={m}
                    isReceiver={!mine}
                    onStatusChange={() => fetchMsg()}
                    onCreateAppointment={(pid) => setApptPatientId(pid)}
                  />
                </div>
              );
            }

            return (
              <div
                key={m.id}
                className={`flex ${
                  mine ? "flex-col items-end" : "flex-col items-start"
                }`}
              >
                <div
                  className={`flex items-end gap-1.5 ${
                    mine ? "justify-end" : "justify-start"
                  } w-full`}
                >
                  {!mine && (
                    <span
                      className={`w-6 h-6 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center ${
                        showAvatar ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {contact.photo_url ? (
                        <img
                          src={contact.photo_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-[9px]">
                          {initials}
                        </span>
                      )}
                    </span>
                  )}
                  <div
                    className={`max-w-[75%] rounded-2xl px-3 py-2 text-[12.5px] ${
                      mine
                        ? "bg-[#0071E3] text-white rounded-br-md"
                        : "bg-[#F2F3F5] text-black rounded-bl-md"
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words">
                      {m.content}
                    </div>
                  </div>
                </div>
                {/* Metadata debajo de la burbuja: hora + visto */}
                <div
                  className={`mt-0.5 flex items-center gap-1 text-[9.5px] text-black/40 ${
                    mine ? "pr-1" : "pl-8"
                  }`}
                >
                  {formatHour(m.created_at)}
                  {mine && (
                    <span
                      className="inline-flex items-center"
                      title={m.read ? "Visto" : "Entregado"}
                    >
                      <Check
                        className={`w-3 h-3 ${
                          m.read ? "text-[#0071E3]" : "text-black/40"
                        } -mr-1.5`}
                        strokeWidth={2.5}
                        weight="bold"
                      />
                      <Check
                        className={`w-3 h-3 ${
                          m.read ? "text-[#0071E3]" : "text-black/40"
                        }`}
                        strokeWidth={2.5}
                        weight="bold"
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {peerTyping && (
            <div
              className="flex items-end gap-1.5 justify-start"
              data-testid={`chat-window-typing-bubble-${contact.id}`}
            >
              <span className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center">
                {contact.photo_url ? (
                  <img
                    src={contact.photo_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-[9px]">
                    {initials}
                  </span>
                )}
              </span>
              <div className="bg-[#F2F3F5] rounded-2xl rounded-bl-md px-3 py-2.5 inline-flex items-center gap-1 text-black/50">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input bar — ícono referido + pill + enviar */}
        <form
          onSubmit={send}
          className="p-2.5 flex items-center gap-1.5 bg-white"
        >
          {canReferTo ? (
            <button
              type="button"
              onClick={() => setShowReferralModal(true)}
              data-testid={`chat-window-referral-${contact.id}`}
              title="Enviar referido a este doctor"
              className="w-9 h-9 rounded-full bg-white border border-black/[0.08] flex items-center justify-center text-black/60 hover:bg-[#0071E3]/[0.06] hover:text-[#0071E3] hover:border-[#0071E3]/40 transition flex-shrink-0"
            >
              <Paperclip className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="Los referidos solo se envían entre doctores"
              className="w-9 h-9 rounded-full bg-white border border-black/[0.08] flex items-center justify-center text-black/30 flex-shrink-0 opacity-60 cursor-not-allowed"
            >
              <Paperclip className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          )}
          <input
            data-testid={`chat-window-input-${contact.id}`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (e.target.value.trim()) notifyTyping();
            }}
            placeholder="Escribe un mensaje…"
            className="flex-1 min-w-0 bg-[#F2F3F5] rounded-full px-4 h-9 outline-none text-[12.5px] text-black placeholder-black/40 focus:ring-2 focus:ring-[#0071E3]/30 transition"
          />
          <button
            data-testid={`chat-window-send-${contact.id}`}
            type="submit"
            disabled={!text.trim()}
            className="w-9 h-9 rounded-full bg-[#0071E3] text-white flex items-center justify-center hover:bg-[#0077ED] disabled:opacity-40 transition flex-shrink-0"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>
        </form>
      </div>

      {showReferralModal && (
        <ReferralQuickModal
          toDoctor={contact}
          onClose={() => setShowReferralModal(false)}
          onSent={() => fetchMsg()}
        />
      )}
      {apptPatientId && (
        <NewAppointmentModal
          open={!!apptPatientId}
          onClose={() => setApptPatientId(null)}
          onCreated={() => {
            setApptPatientId(null);
          }}
          prefillPatientId={apptPatientId}
        />
      )}
    </>
  );
}
